import { Photo } from "@/components/Photo";
import { HospitalityMarkIcon } from "@/components/icons";
import { Eyebrow } from "@/components/Eyebrow";
import { SectionHeader } from "@/components/SectionHeader";
import experience from "@/assets/hospitality/experience.webp?w=640;960;1280;1610&format=webp&as=meta";
import impact from "@/assets/hospitality/impact.webp?w=640;960;1280;1610&format=webp&as=meta";
import operations from "@/assets/hospitality/operations.webp?w=640;960;1280;1610&format=webp&as=meta";
import { useElementHeight } from "@/hooks/useElementHeight";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { responsiveImage } from "@/lib/responsiveImage";
import { useEffect, useRef, useState, type CSSProperties } from "react";

const PANELS = [
  {
    image: responsiveImage(experience),
    eyebrow: "Experience",
    title: "Create a stay that feels effortless",
    body: "Somo connects the systems behind the guest experience so spaces can respond more naturally to the people within them. The result is a stay that feels more intuitive, automated, personalized, and considered without adding more interactions.",
  },
  {
    image: responsiveImage(operations),
    eyebrow: "Operation",
    title: "Make every property work smarter",
    body: "Somo automates routine operations and provides greater visibility into how spaces and systems are performing. Teams can reduce labor and operating costs, catch issues earlier, and better protect the assets behind every property.",
  },
  {
    image: responsiveImage(impact),
    eyebrow: "Impact",
    title: "Use less without compromising more",
    body: "Somo helps properties use energy and resources efficiently, reducing consumption and costs without compromising comfort or service. Smarter lighting, HVAC, and connected systems create value for the business and environment.",
  },
];

export function HospitalityPanels() {
  // Mirrors the `hoverable` screen in tailwind.config.js.
  const expandable = useMediaQuery("(hover: hover) and (min-width: 1024px)");
  const [active, setActive] = useState<string | null>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const scrolling = useRef(false);

  // CSS :hover also fires when content scrolls under a still pointer, so hover
  // follows real pointer movement instead.
  useEffect(() => {
    if (!expandable) {
      return;
    }
    const onScroll = () => {
      scrolling.current = true;
    };
    let last = { x: -1, y: -1 };
    // Some browsers dispatch a synthetic move after scrolling; only a changed
    // position counts as the pointer moving.
    const onPointerMove = (event: PointerEvent) => {
      if (event.clientX === last.x && event.clientY === last.y) {
        return;
      }
      last = { x: event.clientX, y: event.clientY };
      if (!scrolling.current) {
        return;
      }
      scrolling.current = false;
      if (!rowRef.current?.contains(event.target as Node)) {
        setActive(null);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pointermove", onPointerMove, { capture: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pointermove", onPointerMove, {
        capture: true,
      });
    };
  }, [expandable]);

  return (
    <section className="relative flex flex-col gap-16 border-t md:mt-12 border-primary px-2 sm:px-4 md:pb-24 lg:pb-32">
      <div className="relative -mx-2 px-6 pt-20 sm:-mx-4 md:pt-24 lg:pt-32">
        <GridLine className="left-[17.5%]" />
        <GridLine className="left-[82.4%]" />
        <IntroCopy />
      </div>
      <div
        ref={rowRef}
        data-tone="dark"
        onPointerLeave={() => {
          if (!scrolling.current) {
            setActive(null);
          }
        }}
        className="mx-auto flex w-full max-w-screen-2xl flex-col gap-4 lg:h-128 lg:flex-row"
      >
        {PANELS.map((panel) => (
          <Panel
            key={panel.title}
            panel={panel}
            expandable={expandable}
            active={active === panel.title}
            onPointerMove={() => {
              if (!scrolling.current && active !== panel.title) {
                setActive(panel.title);
              }
            }}
          />
        ))}
      </div>
    </section>
  );
}

function Panel({
  panel,
  expandable,
  active,
  onPointerMove,
}: {
  panel: (typeof PANELS)[number];
  expandable: boolean;
  active: boolean;
  onPointerMove: () => void;
}) {
  const [bodyRef, bodyHeight] = useElementHeight<HTMLParagraphElement>();
  // Focusable so keyboard users can expand a panel the way hover does; the
  // body is only visually hidden, so screen readers always read it.
  return (
    <article
      tabIndex={expandable ? 0 : undefined}
      data-active={active || undefined}
      onPointerMove={onPointerMove}
      className="group relative isolate flex h-110 flex-col md:h-96 justify-end overflow-hidden rounded-card p-8 text-inverse outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 lg:h-auto lg:min-w-0 lg:flex-1 hoverable:p-10 hoverable:motion-safe:transition-[flex-grow] hoverable:motion-safe:duration-400 hoverable:motion-safe:ease-out hoverable:data-[active]:flex-[1.8] hoverable:focus-visible:flex-[1.8]"
    >
      {/* Sized for the hover-expanded panel, so it never upgrades mid-hover. */}
      <Photo
        {...panel.image}
        sizes="(min-width: 1024px) min(48vw, 712px), 100vw"
        alt=""
        tone="dark"
        className="absolute inset-0 -z-10"
      />
      <div className="absolute inset-0 -z-10 bg-black/40 transition-colors duration-400 ease-out motion-reduce:duration-150 hoverable:group-data-[active]:bg-black/20 hoverable:group-focus-visible:bg-black/20" />
      {/* At rest the block sits one body-height lower, so the title rests on
          the panel's padding and the hidden body hangs below it; revealing is a
          transform, so nothing reflows while the panel grows. */}
      <div
        className="hoverable:translate-y-[var(--body-height)] hoverable:motion-safe:transition-transform hoverable:motion-safe:duration-400 hoverable:motion-safe:ease-out hoverable:group-data-[active]:translate-y-0 hoverable:group-focus-visible:translate-y-0"
        style={{ "--body-height": `${bodyHeight}px` } as CSSProperties}
      >
        <Eyebrow tone="inherit">{panel.eyebrow}</Eyebrow>
        {/* Fixed per breakpoint and never wider than a shrunk panel, so titles
            don't rewrap while panels grow and shrink. */}
        <h3 className="card-title mt-7 max-w-44 md:max-w-60 hoverable:w-40 hoverable:max-w-none hoverable:xl:w-60">
          {panel.title}
        </h3>
        {/* Fixed width, so the text doesn't rewrap while the panel grows. */}
        <p
          ref={bodyRef}
          className="card-body pt-4 hoverable:w-80 hoverable:opacity-0 hoverable:transition-opacity hoverable:duration-400 hoverable:ease-out hoverable:group-data-[active]:opacity-100 hoverable:group-focus-visible:opacity-100 hoverable:xl:w-120"
        >
          {panel.body}
        </p>
      </div>
    </article>
  );
}

function IntroCopy() {
  return (
    <SectionHeader
      variant="centered"
      className="relative"
      icon={<HospitalityMarkIcon className="size-10 text-brand" />}
      title="Designed for Hospitality, Tuned to Every Property"
      body="Somo is built to improve what guests experience, how properties perform, and how efficiently they use the resources behind every stay."
    />
  );
}

function GridLine({ className }: { className: string }) {
  return (
    <span
      aria-hidden
      className={`pointer-events-none absolute inset-y-0 hidden w-px bg-gradient-to-b from-line to-transparent lg:block ${className}`}
    />
  );
}
